import { CheckIcon } from "@/Icons/v2/Check";
import { useTrans } from "@/translations";

import { useCallback, useMemo, useState } from "react";

import type { Terminal } from "server/dwhApi/terminals/types.js";
import { twMerge } from "tailwind-merge";

type Props = {
  terminals: Terminal[];
  selectedBuildingIds: string[] | null;
  setBuildingTypesSelected: (value: string[] | null) => void;
  onChange: (terminalIds: string[] | null) => void;
};

export const BuildingListWithSearch: React.FC<Props> = ({
  terminals,
  selectedBuildingIds,
  setBuildingTypesSelected,
  onChange,
}) => {
  const { t } = useTrans();
  const [search, setSearch] = useState("");

  const q = useMemo(() => search.trim().toLowerCase(), [search]);

  const filteredTerminals = useMemo(
    () =>
      terminals
        .filter((term) => !q || term.terminalName.toLowerCase().includes(q))
        .sort((a, b) => a.terminalName.localeCompare(b.terminalName)),
    [terminals, q],
  );

  const allIds = useMemo(
    () => terminals.map((term) => term.terminalId),
    [terminals],
  );

  const toggleBuilding = useCallback(
    (id: string) => {
      const base = selectedBuildingIds
        ? selectedBuildingIds.slice()
        : allIds.slice();
      const idx = base.indexOf(id);

      if (idx >= 0) {
        base.splice(idx, 1);
      } else {
        base.push(id);
      }

      onChange(base);
      setBuildingTypesSelected(null);
    },
    [allIds, onChange, selectedBuildingIds, setBuildingTypesSelected],
  );

  return (
    <>
      <div className="border-grey2 border-t p-[8.25px]">
        <input
          value={search}
          placeholder={t("portfolioPage:buildingSelectorSearchBuildings")}
          onChange={(e) => setSearch(e.target.value)}
          className="border-grey3 w-full rounded-md border px-[10px] py-2 text-[13px] outline-none"
        />
      </div>

      <div className="max-h-[220px] overflow-y-auto">
        {filteredTerminals.length ? (
          filteredTerminals.map((term) => {
            const on =
              !selectedBuildingIds ||
              selectedBuildingIds.includes(term.terminalId);
            return (
              <div
                key={term.terminalId}
                onClick={() => toggleBuilding(term.terminalId)}
                className={twMerge(
                  "flex cursor-pointer items-center gap-[10px] px-[12px] py-2",
                  on ? "bg-[#FBFBE9]" : "hover:bg-grey1",
                )}
              >
                <span
                  className={twMerge(
                    "flex h-[15px] w-[15px] flex-none items-center justify-center rounded border-[1.5px] text-[10px] text-white",
                    on ? "border-black bg-black" : "border-grey4 bg-white",
                  )}
                >
                  {on && <CheckIcon />}
                </span>
                <span className="flex-1 truncate text-[13px]">
                  {term.terminalName}
                </span>
                <span className="text-grey6 text-[12px]">
                  {t(`portfolioPage:${term.commercialTerminalCategory?.name}`)}
                </span>
              </div>
            );
          })
        ) : (
          <div className="text-grey6 px-[13px] py-4 text-[13px]">
            {t("portfolioPage:buildingSelectionBuildingTypesNoMatches")}
          </div>
        )}
      </div>
    </>
  );
};
